import { useNavigate } from 'react-router-dom';
import { useAccountingSummary } from '@saiens/shared/hooks';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, Hammer, CheckCircle, FileText, Banknote, LayoutGrid } from 'lucide-react';

export function AccountingHubPage() {
  const navigate = useNavigate();
  const { data: summary, isLoading, error } = useAccountingSummary();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg">
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">財會管理</h1>
      </div>

      {error ? (
        <div className="px-4 py-12 text-center text-red-500">載入失敗</div>
      ) : (
        <div className="grid grid-cols-2 gap-3 px-4 py-4">
          <HubCard
            icon={<Hammer className="h-6 w-6 text-yellow-600" />}
            bgColor="bg-yellow-50"
            title="已完工"
            subtitle="未驗收"
            count={summary?.completed_count ?? 0}
            amount={summary?.completed_amount ?? 0}
            onClick={() => navigate('/accounting/completed')}
          />
          <HubCard
            icon={<CheckCircle className="h-6 w-6 text-green-600" />}
            bgColor="bg-green-50"
            title="已驗收"
            subtitle="未開票"
            count={summary?.accepted_count ?? 0}
            amount={summary?.accepted_amount ?? 0}
            onClick={() => navigate('/accounting/accepted')}
          />
          <HubCard
            icon={<FileText className="h-6 w-6 text-blue-600" />}
            bgColor="bg-blue-50"
            title="已開發票"
            subtitle="未收款"
            count={summary?.invoiced_count ?? 0}
            amount={summary?.invoiced_amount ?? 0}
            badge={
              summary && summary.overdue_count > 0
                ? `${summary.overdue_count} 逾期`
                : undefined
            }
            onClick={() => navigate('/accounting/invoiced')}
          />
          <HubCard
            icon={<Banknote className="h-6 w-6 text-emerald-600" />}
            bgColor="bg-emerald-50"
            title="已銷賬"
            subtitle=""
            count={summary?.paid_count ?? 0}
            amount={summary?.paid_amount ?? 0}
            onClick={() => navigate('/accounting/paid')}
          />
          <button
            onClick={() => navigate('/accounting/overview')}
            className="col-span-2 flex items-center gap-4 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
          >
            <div className="rounded-lg bg-indigo-50 p-2.5">
              <LayoutGrid className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="min-w-0 flex-1 text-left">
              <p className="text-sm font-medium text-gray-900">驗收總覽</p>
              <p className="text-xs text-gray-500">依驗收單狀態分類</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">
                {(summary?.draft_count ?? 0) +
                  (summary?.accepted_count ?? 0) +
                  (summary?.invoiced_count ?? 0) +
                  (summary?.paid_count ?? 0)}
              </p>
              <p className="text-sm font-medium text-gray-500">
                {formatAmount(
                  (summary?.draft_amount ?? 0) +
                    (summary?.accepted_amount ?? 0) +
                    (summary?.invoiced_amount ?? 0) +
                    (summary?.paid_amount ?? 0),
                )}
              </p>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

function formatAmount(amount: number): string {
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `$${(amount / 1_000).toFixed(0)}K`;
  }
  return `$${amount.toLocaleString()}`;
}

function HubCard({
  icon,
  bgColor,
  title,
  subtitle,
  count,
  amount,
  badge,
  onClick,
}: {
  icon: React.ReactNode;
  bgColor: string;
  title: string;
  subtitle: string;
  count: number;
  amount: number;
  badge?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
    >
      <div className={`rounded-lg p-2.5 ${bgColor}`}>{icon}</div>
      <div className="text-center">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
      <p className="text-2xl font-bold text-gray-900">{count}</p>
      <p className="text-sm font-medium text-gray-500">{formatAmount(amount)}</p>
      {badge && (
        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
          {badge}
        </span>
      )}
    </button>
  );
}
